/**
 * MultiSelect — 검색 필터 내장 다중 선택 드롭다운
 *
 * 원본 Ant Design <Select mode="multiple"> 를 대체한다.
 * - 선택된 항목을 태그 형식으로 표시
 * - 드롭다운에서 텍스트 검색 후 클릭으로 선택/해제
 * - 외부 클릭 시 자동 닫힘
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Plus, X } from 'lucide-react';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  maxTagCount?: number;
  renderOption?: (option: MultiSelectOption) => ReactNode;
  renderTag?: (option: MultiSelectOption) => ReactNode;
  /** When true, render the dropdown via React portal to escape ancestor
   *  overflow clipping (e.g. inside a `.lpl-content` container with
   *  `overflow: auto`). Off by default to preserve existing behaviour for
   *  callers that may rely on the dropdown sharing a stacking context with
   *  the trigger (modals, drawers). */
  portalled?: boolean;
  /** When true, typed text that doesn't match any option can be added as its own
   *  value (shown as an "Add …" row, or via Enter when nothing matched) — for
   *  fields whose real-world values outgrow a fixed option list (e.g. legacy data
   *  entered before the list existed). Selected custom values render as a plain
   *  tag/row of their own text since they have no matching `MultiSelectOption`. */
  creatable?: boolean;
  /** Minimum width (px) for the `portalled` dropdown — it otherwise matches the
   *  trigger's own width, widening only up to this floor. Default 240 (this
   *  component's original fixed behavior). Pass a lower value (e.g. 0) for a
   *  narrow trigger (a sidebar filter) that must never render wider than itself. */
  minDropdownWidth?: number;
  /** When true, always shows a "Select all" / "Clear all" row directly under the
   *  search input (not just a "Clear all" that only appears once something is
   *  selected) — for callers where "select every option" is a meaningful action
   *  in its own right (e.g. a platform filter), not just Country/Language lists
   *  too long for that to make sense. */
  showSelectAll?: boolean;
}

export default function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  className,
  maxTagCount = 3,
  renderOption,
  renderTag,
  portalled = false,
  creatable = false,
  minDropdownWidth = 240,
  showSelectAll = false,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLInputElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });

  // 외부 클릭 닫기 (portal'd dropdown 포함)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setOpen(false);
      setFilter('');
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // portal'd 드롭다운 위치 계산 — 스크롤/리사이즈 시 재계산
  useEffect(() => {
    if (!open || !portalled) return;
    const updatePos = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [open, portalled]);

  /** Open + 동시에 trigger 위치를 동기 계산하여 첫 페인트의 (0,0) 깜빡임 방지 */
  const handleTriggerClick = useCallback(() => {
    setOpen((v) => {
      const next = !v;
      if (next && portalled) {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (rect) setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
      return next;
    });
  }, [portalled]);

  // 드롭다운 열릴 때 검색창 포커스
  useEffect(() => {
    if (open) {
      setTimeout(() => filterRef.current?.focus(), 50);
    }
  }, [open]);

  const filteredOptions = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return options;
    const matched = options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    );
    // 정확 매칭(value 또는 label)을 상위로 정렬
    matched.sort((a, b) => {
      const aExact = a.value.toLowerCase() === q || a.label.toLowerCase() === q ? 0 : 1;
      const bExact = b.value.toLowerCase() === q || b.label.toLowerCase() === q ? 0 : 1;
      if (aExact !== bExact) return aExact - bExact;
      const aStart = a.value.toLowerCase().startsWith(q) || a.label.toLowerCase().startsWith(q) ? 0 : 1;
      const bStart = b.value.toLowerCase().startsWith(q) || b.label.toLowerCase().startsWith(q) ? 0 : 1;
      return aStart - bStart;
    });
    return matched;
  }, [options, filter]);

  const [highlightIdx, setHighlightIdx] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  // 방향키로 목록 탐색 중인지 여부 — Space 체크 동작 활성화에 사용
  const isNavigatingRef = useRef(false);

  // 필터 변경 시 하이라이트 초기화 + 탐색 상태 해제
  useEffect(() => { setHighlightIdx(0); isNavigatingRef.current = false; }, [filter]);

  const toggle = useCallback((val: string) => {
    onChange(
      value.includes(val) ? value.filter((v) => v !== val) : [...value, val]
    );
  }, [value, onChange]);

  // Typed text becomes addable once it doesn't already match a selected value or a known option.
  const trimmedFilter = filter.trim();
  const canAddCustom = creatable && trimmedFilter.length > 0 &&
    !value.some((v) => v.toLowerCase() === trimmedFilter.toLowerCase()) &&
    !options.some((o) => o.value.toLowerCase() === trimmedFilter.toLowerCase() || o.label.toLowerCase() === trimmedFilter.toLowerCase());

  const addCustom = useCallback(() => {
    const v = filter.trim();
    if (!v) return;
    onChange([...value, v]);
    setFilter('');
  }, [filter, value, onChange]);

  const remove = useCallback((val: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== val));
  }, [value, onChange]);

  // 하이라이트된 항목이 보이도록 스크롤
  const scrollToHighlight = useCallback((idx: number) => {
    const container = listRef.current;
    if (!container) return;
    const items = container.querySelectorAll('[data-ms-item]');
    items[idx]?.scrollIntoView({ block: 'nearest' });
  }, []);

  const visibleTags = value.slice(0, maxTagCount);
  const hiddenCount = value.length - visibleTags.length;

  return (
    <div ref={containerRef} className={`relative ${className ?? ''}`}>
      {/* 트리거 */}
      <div
        ref={triggerRef}
        onClick={handleTriggerClick}
        className={`select-trigger flex-wrap gap-1 ${open ? 'select-trigger--open' : ''}`}
      >
        {value.length === 0 ? (
          <span className="select-trigger__text select-trigger__text--empty flex-1">{placeholder}</span>
        ) : (
          <>
            {visibleTags.map((v) => {
              const option = options.find((o) => o.value === v);
              return (
                <span
                  key={v}
                  className="inline-flex items-center gap-0.5 rounded-ggc-pill bg-ggc-blue-light px-1 py-0.5 text-[11px] font-medium text-ggc-cta-blue"
                >
                  {option ? (renderTag ? renderTag(option) : option.label) : v}
                <button
                  onClick={(e) => remove(v, e)}
                  className="ml-0.5 rounded-ggc-pill hover:bg-blue-200"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
              );
            })}
            {hiddenCount > 0 && (
              <span className="rounded-ggc-pill bg-ggc-bg-base px-1.5 py-0.5 text-xs text-ggc-text-gray">
                +{hiddenCount}
              </span>
            )}
          </>
        )}
        <ChevronDown
          className={`ml-auto h-4 w-4 shrink-0 text-ggc-text-gray-light transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </div>

      {/* 드롭다운 — portalled=true 일 때는 body에 portal로 띄워 ancestor overflow clipping 회피 */}
      {open && (() => {
        const dropdownContent = (
          <div
            ref={dropdownRef}
            // Marker so overlay outside-click handlers (e.g. the collapsed-sidebar flyout) can
            // exempt clicks inside this body-portaled dropdown instead of closing themselves.
            data-ms-portal="true"
            className={portalled
              ? 'rounded-md border border-border bg-white shadow-ggc-lg'
              : 'absolute z-50 mt-1 w-full rounded-md border border-border bg-white shadow-ggc-lg'}
            style={portalled ? {
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: Math.max(dropdownPos.width, minDropdownWidth),
              // Above the collapsed-sidebar flyout (1200) and Dialog overlay/content (1200/1201)
              // so the dropdown isn't hidden behind them; still below toast/snackbar tiers.
              zIndex: 1250,
            } : { minWidth: minDropdownWidth }}
          >
          {/* 검색 */}
          <div className="border-b border-border p-2">
            <input
              ref={filterRef}
              value={filter}
              onChange={(e) => { isNavigatingRef.current = false; setFilter(e.target.value); }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  isNavigatingRef.current = true;
                  setHighlightIdx((prev) => {
                    const next = Math.min(prev + 1, filteredOptions.length - 1);
                    scrollToHighlight(next);
                    return next;
                  });
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  isNavigatingRef.current = true;
                  setHighlightIdx((prev) => {
                    const next = Math.max(prev - 1, 0);
                    scrollToHighlight(next);
                    return next;
                  });
                } else if (e.key === 'Enter' && filteredOptions.length > 0) {
                  e.preventDefault();
                  toggle(filteredOptions[highlightIdx]?.value ?? filteredOptions[0].value);
                  setFilter('');
                } else if (e.key === 'Enter' && canAddCustom) {
                  e.preventDefault();
                  addCustom();
                } else if (e.key === ' ' && (filter === '' || isNavigatingRef.current) && filteredOptions.length > 0) {
                  e.preventDefault();
                  toggle(filteredOptions[highlightIdx]?.value ?? filteredOptions[0].value);
                } else if (e.key === 'Escape') {
                  setOpen(false);
                  setFilter('');
                }
              }}
              placeholder="Search..."
              className="w-full rounded-ggc-sm border border-input px-2 py-1 text-xs outline-none focus:border-ggc-cta-blue"
            />
          </div>

          {/* 선택 요약 — showSelectAll이면 선택 개수와 무관하게 항상 표시(Select all 포함),
              아니면 기존처럼 뭔가 선택됐을 때만 Clear all만 표시. */}
          {(showSelectAll || value.length > 0) && (
            <div className="flex items-center justify-between border-b border-border px-3 py-1">
              <span className="text-xs text-ggc-text-gray">{value.length} selected</span>
              <div className="flex items-center gap-2">
                {showSelectAll && (
                  <button
                    onClick={() => onChange(options.map((o) => o.value))}
                    className="text-xs text-ggc-cta-blue hover:underline"
                  >
                    Select all
                  </button>
                )}
                <button
                  onClick={() => onChange([])}
                  className="text-xs text-ggc-red hover:underline"
                >
                  Clear all
                </button>
              </div>
            </div>
          )}

          {/* 목록 */}
          <div ref={listRef} className="max-h-60 overflow-y-auto">
            {canAddCustom && (
              <button
                type="button"
                onClick={addCustom}
                className="flex w-full items-center gap-2 border-b border-border px-3 py-1.5 text-left text-xs text-ggc-cta-blue hover:bg-muted/40"
              >
                <Plus className="h-3.5 w-3.5 shrink-0" />
                <span className="flex-1 truncate">Add &quot;{trimmedFilter}&quot;</span>
              </button>
            )}
            {filteredOptions.length === 0 && !canAddCustom ? (
              <div className="py-6 text-center text-xs text-ggc-text-gray">No options found</div>
            ) : (
              filteredOptions.map((o, i) => {
                const isSelected = value.includes(o.value);
                const isHighlighted = i === highlightIdx;
                return (
                  <button
                    key={o.value}
                    data-ms-item
                    onClick={() => toggle(o.value)}
                    onMouseEnter={() => setHighlightIdx(i)}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition ${
                      isHighlighted ? 'bg-muted/60' : 'hover:bg-muted/40'
                    } ${isSelected ? 'bg-ggc-blue-light text-ggc-cta-blue' : 'text-foreground'}`}
                  >
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-ggc-sm border ${
                        isSelected ? 'border-ggc-cta-blue bg-blue-600 text-white' : 'border-ggc-border-gray-dark'
                      }`}
                    >
                      {isSelected && <Check className="h-3 w-3" />}
                    </span>
                    <span className="flex-1 truncate">
                      {renderOption ? renderOption(o) : o.label}
                    </span>
                    {!renderOption && <span className="shrink-0 text-xs text-ggc-text-gray">{o.value}</span>}
                  </button>
                );
              })
            )}
          </div>
        </div>
        );
        return portalled ? createPortal(dropdownContent, document.body) : dropdownContent;
      })()}
    </div>
  );
}
